import { useState } from "react"

import { Datasets } from "../../../../constants/enums"
import { parseErrorAsObj } from "../../../../errors/errorProcessors"
import { IAppError } from "../../../../errors/errorTypes"
import { useV2gCommentRouting } from "../../../../hooks/useV2gCommentRouting"
import { postClientProtected } from "../../clients/clients"
import {
    TTR_CREATE_COMMENT,
    LCT_CREATE_COMMENT,
    V2G_PUBLISH_LCT_COMMENT,
} from "../../urls/private"

interface ICreateCommentHook {
    isSending: boolean
    error: IAppError | undefined
    postResponse: (
        deviceSystemRef: string,
        comment: string,
        dataset?: Datasets
    ) => Promise<void>
}

/**
 * Provides functions and logic for posting a create comment request
 * on a TTR/LCT device. Pass `isV2gDevice` when the device is a V2G EVCP so
 * that V2G assessors are routed to the `/v2g/v1/` endpoints.
 */
const useCreateComment = (isV2gDevice = false): ICreateCommentHook => {
    const [isSending, setIsSending] = useState(false)
    const [error, setError] = useState<IAppError | undefined>(undefined)
    const useAsV2gAssessor = useV2gCommentRouting(isV2gDevice)

    const postResponse = async (
        deviceSystemRef: string,
        comment: string,
        dataset?: Datasets
    ): Promise<void> => {
        try {
            setIsSending(true)

            const body = {
                deviceSystemRef,
                comment,
            }
            if (
                dataset &&
                ![Datasets.TTR, Datasets.TTR_DRAFT].includes(dataset)
            ) {
                const url = useAsV2gAssessor
                    ? V2G_PUBLISH_LCT_COMMENT(dataset)
                    : LCT_CREATE_COMMENT(dataset)
                await postClientProtected(url, body)
            } else {
                await postClientProtected(TTR_CREATE_COMMENT, body)
            }
            setError(undefined)
        } catch (err) {
            const errorObj = parseErrorAsObj(err)
            setError(errorObj)
        } finally {
            setIsSending(false)
        }
    }

    return {
        error,
        isSending,
        postResponse,
    }
}

export default useCreateComment
